import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import { BatchReturnStatusType } from 'returnCenter-api';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { returnSelectors } from 'store/reducers/returnSlice';
import { setSelectedBatchStatus } from 'store/reducers/navigationSlice';
import { batchReturnService } from 'services/batchReturnService';

/**
 * On a slip received from a batch: the way back to the batch, where the rest of its packages are
 * followed up.
 */
const BatchSourceCard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const returnSlip = useAppSelector(returnSelectors.selectReturnSlip);
    const batchReturnId = returnSlip?.batchReturnId;
    const [comment, setComment] = useState<string | null>(null);

    // The slip carries only the batch id; the comment is the sender's note on the batch.
    useEffect(() => {
        setComment(null);
        if (!batchReturnId) return;

        let cancelled = false;
        batchReturnService
            .getBatchReturnById(batchReturnId)
            .then((batch) => {
                if (!cancelled) setComment(batch.comment ?? null);
            })
            .catch(() => {
                // The link still works without the comment.
            });
        return () => {
            cancelled = true;
        };
    }, [batchReturnId]);

    if (!batchReturnId) {
        return null;
    }

    const openBatch = () => {
        dispatch(setSelectedBatchStatus(BatchReturnStatusType.Incoming));
        navigate(`/batch-returns/incoming/${batchReturnId}`);
    };

    return (
        <Flex $grow={1} column className="batch-source-wrapper">
            <Wrapper column gap="0.25rem">
                <Text margin="0" fontSize={12} bold as="span">
                    {`${t('batchReturn.headerLabel', 'Batch return')} `}
                    <LinkButton type="button" onClick={openBatch}>
                        #{batchReturnId}
                    </LinkButton>
                </Text>
                {comment && (
                    <Text margin="0" fontSize={12}>
                        {comment}
                    </Text>
                )}
            </Wrapper>
        </Flex>
    );
};

export default BatchSourceCard;

const Wrapper = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    padding: 1rem;
    border-radius: var(--radius);
`;

const LinkButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.link};
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    padding: 0;
    text-decoration: underline;
    text-align: left;

    &:hover {
        opacity: 0.7;
    }
`;
